export class HelpCenterV2 extends HTMLElement {
  #searchTimeout = null;
  #abortController = null;

  connectedCallback() {
    this.tabList = this.querySelector('[role="tablist"]');
    this.layout = this.querySelector('[data-help-center-v2-layout]');
    this.searchInput = this.querySelector('[data-help-center-v2-search]');
    this.searchResults = this.querySelector('[data-help-center-v2-search-results]');
    this.tabPanels = this.querySelectorAll('[data-help-center-v2-panel]');
    this.noResults = this.querySelector('[data-help-center-v2-no-results]');

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (this.tabList) {
      this.tabList.addEventListener(
        'click',
        (event) => {
          const btn = event.target.closest('[aria-controls]');
          if (!btn) return;
          this.#activateTab(btn);
        },
        { signal },
      );
    }

    // Only wire up search if its result/no-results containers exist (page mode
    // with zero categories renders the input but skips those containers).
    if (this.searchInput && this.searchResults && this.noResults) {
      this.searchInput.addEventListener(
        'input',
        () => {
          clearTimeout(this.#searchTimeout);
          this.#searchTimeout = setTimeout(() => {
            this.#handleSearch(this.searchInput.value.trim());
          }, 250);
        },
        { signal },
      );
    }

    // Quick-link anchors jump into a category panel and open the matching question
    this.addEventListener(
      'click',
      (event) => {
        const anchor = event.target.closest('a[href*="#"]');
        if (!anchor || !this.contains(anchor)) return;

        const url = new URL(anchor.href, window.location.href);
        if (url.pathname !== window.location.pathname) return;

        const targetId = url.hash?.substring(1);
        if (!targetId) return;

        const target = document.getElementById(targetId);
        if (!target) return;

        event.preventDefault();

        // Exit search mode first so the target isn't hidden behind display:none
        // layout and getElementById can't resolve to a hidden original while a
        // same-id clone sits in the (still-attached) search results.
        if (this.searchInput && this.searchInput.value !== '') {
          this.searchInput.value = '';
          this.#clearSearch();
        }

        const panel = target.closest('[data-help-center-v2-panel]');
        if (panel && this.tabList) {
          const controller = this.tabList.querySelector(`[aria-controls="${panel.id}"]`);
          if (controller) this.#activateTab(controller);
        }

        if (target.tagName === 'DETAILS') target.open = true;

        this.#scrollTo(target);
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    clearTimeout(this.#searchTimeout);
  }

  #scrollTo(element) {
    const header = document.querySelector('.shopify-section--header');
    const offset = header ? header.offsetHeight + 10 : 10;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  #activateTab(controller) {
    const tabId = controller.getAttribute('aria-controls');
    if (!tabId || !this.tabList) return;

    const allControllers = this.tabList.querySelectorAll('[aria-controls]');

    for (const btn of allControllers) {
      btn.ariaSelected = btn.getAttribute('aria-controls') === tabId ? 'true' : 'false';
    }

    for (const panel of this.tabPanels) {
      panel.ariaHidden = panel.id !== tabId ? 'true' : 'false';
    }
  }

  #handleSearch(term) {
    if (term === '') {
      this.#clearSearch();
      return;
    }

    const lowerTerm = term.toLowerCase();
    const matchingItems = [];

    for (const panel of this.tabPanels) {
      const questions = panel.querySelectorAll('[data-help-center-v2-question]');

      for (const question of questions) {
        const heading = question.querySelector('[data-help-center-v2-question-heading]');
        const content = question.querySelector('[data-help-center-v2-question-content]');

        const headingText = heading ? heading.textContent.toLowerCase() : '';
        const contentText = content ? content.textContent.toLowerCase() : '';

        if (headingText.includes(lowerTerm) || contentText.includes(lowerTerm)) {
          const clone = question.cloneNode(true);
          clone.open = true;
          // Strip ids so the clone never collides with the (still-attached,
          // just hidden) original — a duplicate id would make getElementById
          // resolve anchor clicks to the hidden source instead of this clone.
          clone.removeAttribute('id');
          for (const el of clone.querySelectorAll('[id]')) el.removeAttribute('id');
          matchingItems.push(clone);
        }
      }
    }

    this.#showSearchResults(matchingItems);
  }

  #showSearchResults(items) {
    if (!this.searchResults || !this.noResults) return;

    if (this.layout) this.layout.style.display = 'none';

    this.searchResults.innerHTML = '';

    if (items.length === 0) {
      this.noResults.ariaHidden = 'false';
      this.searchResults.ariaHidden = 'true';
    } else {
      this.noResults.ariaHidden = 'true';
      this.searchResults.ariaHidden = 'false';

      for (const item of items) {
        this.searchResults.appendChild(item);
      }
    }
  }

  #clearSearch() {
    if (!this.searchResults || !this.noResults) return;

    this.searchResults.innerHTML = '';
    this.searchResults.ariaHidden = 'true';
    this.noResults.ariaHidden = 'true';

    if (this.layout) this.layout.style.display = '';
  }
}

customElements.define('help-center-v2', HelpCenterV2);